import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Card, Col, Form, Input, InputNumber, message, Modal, Row, Select, Spin } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ROW_GUTTER, VALIDATE_REQUIRED } from '@/config';
import { BusEntityType, SeatingType } from '@/constants';
import BusDesign from './busDesign';
import BusSummary from './BusSummary';
import { clearAllSeatingModelsCache } from '@/hooks/data/masterDataHook';
import { CompanyTicketClassDto, CreateUpdateSeatingModelDetailModel, CreateUpdateSeatingModelModel, SeatingModelDto } from '@/__generated';
import { apiSdk } from '@/httpClient';
import { useCanManageFleetTrip } from '@/hooks/useHasPermissions';

const PAGE_TITLE = 'seatModelsPage_Title';

const columns: IColumnType<SeatingModelDto>[] = [
  {
    title: 'seatModelsPage_SeatingModelName',
    dataIndex: 'seatingModelName',
    ellipsis: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'seatModelsPage_SeatingModelRef',
    dataIndex: 'seatingModelRef',
    sorter: true,
    ellipsis: true,
  },
  {
    title: 'seatModelsPage_NoOfRows',
    dataIndex: 'noOfRows',
    ellipsis: true,
  },
  {
    title: 'seatModelsPage_SeatingType',
    dataIndex: 'seatingType',
    render: v => SeatingType.__getValue(v),
  },
];

const filters: Array<IFilterItem> = [
  { type: 'TEXT', name: 'seatingModelName', label: 'seatModelsPage_SeatingModelName' },
  { type: 'LIST', name: 'SeatingType', label: 'seatModelsPage_SeatingType', listData: SeatingType.__getSelectOptions() },
];

const SeatModelsPage = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [editingModel, setEditingModel] = useState<CreateUpdateSeatingModelModel>();
  const [loading, setLoading] = useState(false);
  const [ticketClasses, setTicketClasses] = React.useState<CompanyTicketClassDto[]>([]);
  const [seatsMap, setSeatsMap] = React.useState<CreateUpdateSeatingModelDetailModel[]>([]);

  const canUpdate = useCanManageFleetTrip();
  const clearCache = () => {
    clearAllSeatingModelsCache();
  };
  React.useEffect(() => {
    apiSdk.CompanyTicketClassService.getCompanyTicketClasses({ pageSize: 100 }).then(res => {
      setTicketClasses(res.data || []);
    });
  }, []);

  const confirmSeatData = () => {
    let hasAnyEmptySeat = seatsMap.some(x => x.busEntityType === BusEntityType.Seat && (!x.baseTicketClassId || !x.entityName));
    return new Promise(resolve => {
      if (hasAnyEmptySeat) {
        Modal.confirm({
          title: t('Some seats do not have ticket class or seat name!'),
          content: t('Some seats do not have ticket class or seat name. Do you want to save this data?'),
          okText: t('Submit'),
          cancelText: t('Cancel'),
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      }
      resolve(true);
    });
  };
  const createEntity = values => {
    confirmSeatData().then(res => {
      if (res) {
        setLoading(true);
        apiSdk.SeatingModelService.create({ body: { ...values, seatingModelDetails: seatsMap } })
          .then(response => {
            if (response?.success) {
              clearCache();
              closeAndClearData();

              zTb.current?.reload();
            } else {
              if (response.message) {
                message.error(t(response.message || ''));
              }
            }
          })
          .catch(error => {
            message.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };
  const closeAndClearData = () => {
    setFormVisible(false);
    setEditingModel(undefined);
    setEditingModel(undefined);
    setSeatsMap([]);
  };
  const updateEntity = values => {
    confirmSeatData().then(res => {
      if (res) {
        setLoading(true);
        apiSdk.SeatingModelService.update({ body: { ...values, seatingModelDetails: seatsMap } })
          .then(response => {
            if (response?.success) {
              clearCache();
              closeAndClearData();
              zTb.current?.reload();
            } else {
              if (response.message) {
                message.error(t(response.message || ''));
              }
            }
          })
          .catch(error => {
            message.error(error);
          })
          .finally(() => {
            setLoading(false);
          });
      }
    });
  };
  const onEdit = (id: string) => {
    setLoading(true);
    setFormVisible(true);
    apiSdk.SeatingModelService.getEntityForUpdate({ id: id })
      .then(res => {
        setEditingModel(res);

        form.setFieldsValue(res);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const numberOfRow = Form.useWatch('noOfRows', form);
  const seatingType = Form.useWatch('seatingType', form);
  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={
        canUpdate
          ? [
              <Button
                key="create"
                type="primary"
                className="nta-create-btn"
                icon={<PlusCircleOutlined />}
                onClick={() => {
                  form.resetFields();
                  setEditingModel(undefined);
                  setFormVisible(true);
                }}
              >
                {t('createNew')}
              </Button>,
            ]
          : null
      }
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/fleets/seating-models/get-seating-models"
          bordered
          order_by="createdOn"
          order_by_asc={true}
          allowEdit={canUpdate}
          onEdit={record => {
            onEdit(record.id);
          }}
          allowDelete={canUpdate}
          delete_url="/fleets/seating-models/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        {formVisible ? (
          <Modal
            open={formVisible}
            maskClosable={false}
            width={1500}
            className="nta-custom-form"
            title={!editingModel ? t('seatModelsPage_CreateSeatModel') : t('seatModelsPage_UpdateSeatModel')}
            forceRender={true}
            style={{ top: 10 }}
            okText={t('save')}
            cancelText={t('cancel')}
            okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
            onCancel={() => {
              form.resetFields();
              closeAndClearData();
            }}
            onOk={() => {
              form
                .validateFields()
                .then(values => {
                  if (values.id) {
                    updateEntity(values);
                  } else {
                    createEntity(values);
                  }
                })
                .catch(info => {});
            }}
          >
            <Spin spinning={loading}>
              <Form
                form={form}
                size="middle"
                layout="vertical"
                initialValues={{
                  seatingType: SeatingType['3X3 (7)'],
                  noOfRows: 15,
                }}
              >
                <Form.Item name="id" className="hidden">
                  <Input type="hidden" />
                </Form.Item>

                <Row gutter={ROW_GUTTER}>
                  <Col md={6}>
                    <Card className="mtopo-card" title={t('seatModelsPage_BusBasicInfo')} size="small">
                      <Form.Item label={t('seatModelsPage_SeatingModelRef')} name="seatingModelRef">
                        <Input disabled className="input-value-color" />
                      </Form.Item>
                      <Form.Item
                        label={t('seatModelsPage_SeatingModelName')}
                        name="seatingModelName"
                        rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                      >
                        <Input />
                      </Form.Item>
                      <Form.Item label={t('seatModelsPage_NoOfRows')} name="noOfRows" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <InputNumber min={0} disabled={!!editingModel} className="input-value-color w-100" />
                      </Form.Item>
                      <Form.Item label={t('seatModelsPage_SeatingType')} name="seatingType" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                        <Select options={SeatingType.__getSelectOptions()} disabled={!!editingModel} className="input-value-color" />
                      </Form.Item>
                    </Card>
                    <BusSummary seats={seatsMap} />
                  </Col>
                  <Col md={18}>
                    <BusDesign
                      numberOfRow={numberOfRow}
                      type={seatingType}
                      originSeatsMap={editingModel?.seatingModelDetails}
                      ticketClasses={ticketClasses}
                      onChange={seats => setSeatsMap(seats)}
                    />
                  </Col>
                </Row>
              </Form>
            </Spin>
          </Modal>
        ) : null}
      </div>
    </MainContent>
  );
};
export default SeatModelsPage;
